import { Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { Account, IAccount } from '@services/accounts.service';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { Rule, RuleDefinition, RuleEdit, RulesDataService } from '@services/rules-data.service';
import { combineLatest, distinctUntilChanged, filter, finalize, firstValueFrom, Subject, take, takeUntil, tap } from 'rxjs';
import { convertEpochToDateString } from '@functions/functions'
import { CreateRuleComponent, EditRuleDialogData } from 'src/app/dialogs/create-rule/create-rule.component';
import { EventsDataService, RuleEvent } from '@services/events-data.service';
import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';

/** Component */
@Component({
	selector: 'app-rule-details',
	templateUrl: './rule-details.component.html',
	styleUrls: ['./rule-details.component.scss']
})
export class RuleDetailsComponent {

	/** Rule */
	public rule: Rule;

	/** Asset */
	public asset: LevelAsset;

	/** rule Events */
	public ruleEvents: RuleEvent[] = [];

	/** Filtered Rule Events */
	public filteredRuleEvents: RuleEvent[] = [];

	/** Field Columns */
	public fields: Object = { text: 'RuleText', id: 'RuleEventID' };

	/** accountID */
	@Input() accountID: string;
	/** selectedAccount */
	public selectedAccount: IAccount;
	/** OrganisationID */
	@Input() public OrganisationUID: string;

	/** Current User */
	public currentUser: WebAppUser;

	/** Rule Edits */
	public ruleEdits: RuleEdit[] = [];

	/**
	 * Default Constructor
	 * @param rulesDataService rulesDataService
	 * @param eventsDataService eventsDataService
	 * @param assetsDataService assetsDataService
	 * @param dialog dialog
	 * @param authenticationService authenticationService
	 * @param siteSubscriptions siteSubscriptions
	 * @param router router
	 * @param globalFunctions globalFunctions
	 * @param location Location
	 */
	constructor(
		public rulesDataService: RulesDataService,
		public eventsDataService: EventsDataService,
		public assetsDataService: AssetsDataService,
		public dialog: MatDialog,
		public authenticationService: AuthenticationService,
		public siteSubscriptions: siteSubscriptions,
		private router: Router,
		private globalFunctions: GlobalFunctions,
		private location: Location
	) {
		// this.globalFunctions.displaySpinner(true)
		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$,
			this.siteSubscriptions.selectedAccount$,
			this.siteSubscriptions.selectedOrganisationUID$,
			this.authenticationService.currentUser$
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccountGUID, selectedOrganisationUID, account, currentUser]) =>
				(!!selectedAccountGUID && !!selectedOrganisationUID && !!account && !!currentUser)
			),
			take(1)
		).subscribe({
			next: ([selectedAccountGUID, account, selectedOrganisationUID, currentUser]) => {

				this.accountID = selectedAccountGUID;
				this.selectedAccount = account;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;

				this.OrganisationUID = selectedOrganisationUID;

				this.currentUser = currentUser;

				this.getRule();

			},
			error: (error) => {
				if (error instanceof HttpErrorResponse && error.status === 404) { return; }
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * getRule
	 */
	async getRule() {
		try {
			const rules = await firstValueFrom(
				this.rulesDataService.getRules$(this.accountID, this.OrganisationUID, 0, this.getRuleID()).pipe(
					filter(result => !!result),
					tap(() => { }),
					finalize(() => { })
				)
			);
			if (rules) {
				this.rule = rules[0];
				await Promise.all([
					this.getAndPushAssets(),
					this.getRuleEvents(this.rule),
					this.getRuleEdits(this.rule)
				]);
			}
		}
		catch (error) {
			if (error instanceof HttpErrorResponse && error.status === 404) { return; }
			this.globalFunctions.HandleError(error, "Error: this.rulesDataService.getRules$", false);
		}
	}

	/**
	 * getAndPushAssets
	 */
	async getAndPushAssets() {
		try {
			const asset = await firstValueFrom(
				this.assetsDataService.getOrganisationAssets$(this.accountID, this.OrganisationUID, this.rule.AssetID).pipe(
					filter(result => !!result),
					tap(() => { }),
					finalize(() => {
						this.globalFunctions.displaySpinner(false);
					})
				)
			);
			if (asset) {
				// this.assets.push(asset);
				this.asset = asset;
			}
		}
		catch (error: any) {
			if (error instanceof HttpErrorResponse && error.status === 404) { return; }
			this.globalFunctions.HandleError(error, "Error: this.assetsDataService.getOrganisationAssets$", false);
		}
	}

	/**
	 * getAndPushAssets
	 */
	getRuleEvents(rule: Rule) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// for (let i = 0; i < this.rules.length; i++) {
		this.eventsDataService.getRuleEvents$(this.accountID, this.OrganisationUID, rule.RuleID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (events: any) => {
				if (events) {
					// this.assets.push(asset);
					this.ruleEvents = events.filter((x: RuleEvent) => !x.Archived); //issue #92
					this.filteredRuleEvents = this.ruleEvents;
				}
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "ruleDetails Error: Getting Rule Events", false);
				}
			},
			// complete: () => { console.log("myAccounts: Retrieving Accounts: Complete");
		});
		// }
	}

	/** 
	 * getRuleID 
	 */
	getRuleID(): number {
		let url = this.router.url
		let id = url.split('/').pop() || '';
		try {
			let id2: number = parseInt(id)
			return id2
		} catch {
			return 0
		}
	}

	/**
	 * OpenRule
	 * @param rule
	 */
	OpenRule(rule: Rule) {
		var _edit = { ...rule };

		let time: string = this.convertMinutesToHHMM(_edit.TriggerTime)[0] + ":" + this.convertMinutesToHHMM(_edit.TriggerTime)[1]

		let dialogData = <EditRuleDialogData>{
			assetItem: this.asset,
			ruleItem: _edit,
			triggerTime: time,
			comparison: _edit.RuleText.match(/AND|OR/)?.[0] || "",
			readonly: !this.selectedAccount.Admin
		}

		const dialogRef = this.dialog.open(CreateRuleComponent, {
			data: dialogData
		});

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(async result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			if (result.ruleEnabled == null) {
				rule.RuleEnabled = false;
			} else {
				rule.RuleEnabled = result.ruleEnabled
			}
			rule.RuleDescription = result.ruleDescription
			rule.RuleMessage = result.ruleMessage
			rule.TriggerTime = this.timeToMinutes(result.ruleTime)

			//split the rule into the rule-definition composition

			if (rule.RuleDef) {
				const data1 = {
					RuleDefID: rule.RuleDef[0].RuleDefID,
					RuleID: rule.RuleID,
					Condition: 1,
					DatapointID: result.dataPoint1,
					Limit: result.value1,
					Operator: result.condition1
				}
				let definition1: RuleDefinition = data1;

				let definition2 = undefined;
				/** if we already had another definition */
				if (result.dataPoint2 && rule.RuleDef[1]) {
					const data2 = {
						RuleDefID: rule.RuleDef[1].RuleDefID,
						RuleID: rule.RuleID,
						Condition: 2,
						DatapointID: result.dataPoint2,
						Limit: result.value2,
						Operator: result.condition2
					}
					definition2 = data2;
					/** if a new definition is being added */
				} else if (result.dataPoint2 && !rule.RuleDef[1]) {
					const data2 = {
						RuleID: rule.RuleID,
						Condition: 2,
						DatapointID: result.dataPoint2,
						Limit: result.value2,
						Operator: result.condition2
					}
					definition2 = data2;
				}

				if (definition2) {
					rule.RuleText = "[1] " + result.comparison + " [2]";
					rule.RuleDef = [definition1, definition2];

				} else {
					rule.RuleText = "[1]"
					rule.RuleDef = [definition1]
				}
			}

			await this.patchRule(rule)

			// if (this.selectedAsset) { this.updateForm }
			// this.hierarchyChanged.emit(true)
		});
	}

	/**
	 * patchRule
	 * @param rule
	 */
	async patchRule(rule: Rule, deleteRule: boolean = false) {
		try {
			const sqlResult = await firstValueFrom(
				this.rulesDataService.patchRule$(this.accountID,
					this.OrganisationUID, rule.AssetID, rule, this.currentUser.UserID, deleteRule).pipe(
						filter(result => !!result),
						tap(() => { }),
						finalize(() => { })
					)
			);

			this.globalFunctions.DisplaySnackBar({
				message: !deleteRule ? "Rule Updated" : "Rule Deleted",
				type: SnackType.Info,
				duration: 1000
			});
			if (!deleteRule) {
				await this.getRule();
			} else {
				this.goToRules();
			}
		}
		catch (error) {
			if (error instanceof HttpErrorResponse && error.status === 404) { return; }
			this.globalFunctions.HandleError(error, "Error: this.rulesDataService.patchRule$", false);
		}
	}

	/**
	 * Converts int of total minutes to [HH,MM] string array
	 * @param totalMinutes
	 * @returns
	 */
	convertMinutesToHHMM(totalMinutes: number): string[] {
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;
		const hoursString = hours.toString().padStart(2, '0');
		const minutesString = minutes.toString().padStart(2, '0');
		return [hoursString, minutesString]
	}

	/**
	 * Converts the HH:MM format required for the trigger time to an int of
	 * total minutes
	 * @param timeStr
	 * @returns
	 */
	timeToMinutes(timeStr: string): number {
		const [hours, minutes] = timeStr.split(':').map(Number);
		const totalMinutes = hours * 60 + minutes;
		return totalMinutes;
	}

	/**
	 * getDate
	 * @param timestamp 
	 * @returns 
	 */
	getDate(timestamp: number): string {
		return convertEpochToDateString(timestamp)
	}

	/**
	 * GoToEvent
	 * @param event 
	 */
	GoToEvent(event: RuleEvent) {
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/event/${event.RuleEventID}`], {
			queryParams: { eventType: '2' }
		});
	}

	/**
	 * goToRules
	 */
	goToRules() {
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/rules`])
	}

	/** back */
	back() {
		this.location.back();
	}

	/**
	 * onFiltering
	 * @param event
	 */
	onFiltering(event: any): void {
		event.cancel = true;
		const query = event.text.toLowerCase();
		this.filteredRuleEvents = this.ruleEvents.filter(data => {
			const dateStr = `${convertEpochToDateString(data.TimeStamp)}`.toLowerCase();
			const assetInfo = `${data.AssetName || ''}`.toLowerCase();
			const ruleEventID = data.RuleEventID.toString().toLowerCase();

			// Combine the necessary fields for filtering
			const combinedStr = `${dateStr} ${assetInfo} ${ruleEventID}`;
			return combinedStr.includes(query);
		});
	}

	/** getRuleEdits */
	async getRuleEdits(rule: Rule) {
		if (rule.RuleID) {
			try {
				const ruleEdits = await firstValueFrom(
					this.rulesDataService.getRuleEdits$(this.accountID, this.OrganisationUID, rule.RuleID).pipe(
						filter(result => !!result),
						tap(() => { this.ruleEdits = []; }),
						finalize(() => { })
					)
				);
				this.ruleEdits = ruleEdits;
			}
			catch (error) {
				if (error instanceof HttpErrorResponse && error.status === 404) { return; }
				this.globalFunctions.HandleError(error, "Error: this.rulesDataService.getRuleEdits$", false);
			}
		}
	}

	/**
	 * goToAsset
	 */
	goToAsset() {
		this.siteSubscriptions.SetSelectedAsset(this.asset);
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/location/${this.asset.ParentUID}/asset/${this.asset.AssetID}`]);
	}

	/**
	 * checkRuleEdit
	 */
	checkRuleEdit(ruleEdit: RuleEdit): number {
		let result: number = 0;
		let match = ruleEdit.PreviousConditions.split(" ")[0].match(/(\d+\.\d+)/);
		let trunc;
		if (match) {
			trunc = ruleEdit.PreviousConditions.replace(match[1], parseFloat(match[1]).toFixed(1));
		}
		if (ruleEdit.NewConditions == trunc) {
			/** Conditions are the same */
			if (ruleEdit.PreviousEnabled != ruleEdit.NewEnabled) {
				/** Enabled state changed */
				if (ruleEdit.NewEnabled) {
					result = 1; //rule was enabled but not changed
				} else {
					result = 2; //rule was disabled but not changed
				}
			}
			if (ruleEdit.PreviousDataPoints != ruleEdit.NewDataPoints) {
				/** Datapoints changed */
			}
		}
		return result;
	}

	/**
	 * deleteRule
	 */
	deleteRule() {
		this.globalFunctions.UserConfirm$(`Delete ${this.rule.RuleDescription}?`)
			.subscribe(async OKResult => {
				if (OKResult) {
					await this.patchRule(this.rule, true);
				}
			});
	}
}
